import { tk } from '../i18n/translate.js';

// Which roles may open each signed-in route. Single source for the route guards (App.jsx) and the navigation menus,
// so a menu never offers a page the user would be refused.
export const ROUTE_ROLES = {
  '/user-directory': ['admin'],
  '/roles-permissions': ['admin'],
  '/master-item-catalogue': ['admin', 'customer_admin', 'storekeeper'],
  '/inwarding-asset-tagging': ['admin', 'storekeeper'],
  '/tag-generation-binding-console': ['admin', 'storekeeper'],
  // Asset detail + timeline and the scan landing page (/t/:code) are open to every web role that can see assets.
  '/asset-detail': ['admin', 'customer_admin', 'property_manager', 'finance_controller', 'auditor', 'department_head', 'storekeeper', 'field_user'],
  '/t': ['admin', 'customer_admin', 'property_manager', 'finance_controller', 'auditor', 'department_head', 'storekeeper', 'field_user'],
  // Field app (SRS 4.2): Scan home, Asset quick view and Sync status - every role that works with assets in the field.
  '/scan': ['admin', 'customer_admin', 'property_manager', 'finance_controller', 'auditor', 'department_head', 'storekeeper', 'field_user'],
  '/asset-quick-view': ['admin', 'customer_admin', 'property_manager', 'finance_controller', 'auditor', 'department_head', 'storekeeper', 'field_user'],
  '/sync-status': ['admin', 'customer_admin', 'property_manager', 'finance_controller', 'auditor', 'department_head', 'storekeeper', 'field_user'],
  '/stock-points-par-management': ['admin', 'storekeeper'],
  // AOS-STK-007: the laundry dashboard on its own page (department heads and auditors read-only).
  '/linen-laundry': ['admin', 'storekeeper', 'property_manager', 'department_head', 'auditor'],
  // AOS-KIT-006: opening inventory for the Property Manager and Customer Admin (stores use the Inwarding page).
  '/opening-inventory': ['admin', 'customer_admin', 'property_manager'],
  '/asset-custody-movements': ['admin', 'property_manager', 'department_head', 'storekeeper', 'auditor'],
  '/first-scan-walk-binding': ['admin', 'field_user'],
  '/blind-counts-variance-console': ['admin', 'field_user', 'property_manager', 'storekeeper', 'auditor'],
  '/depreciation-runs-gl-schedules': ['admin', 'finance_controller', 'auditor', 'property_manager'],
  '/asset-disposals-write-offs': ['admin', 'finance_controller', 'department_head', 'property_manager', 'auditor'],
  '/asset-valuation-analytics-dashboard': ['admin', 'customer_admin', 'finance_controller', 'auditor', 'property_manager', 'department_head', 'storekeeper', 'field_user', 'user'],
  '/physical-audit-verification': ['admin', 'field_user'],
  '/audit-reconciliation-console': ['admin', 'property_manager', 'finance_controller', 'auditor'],
  '/audit-exception-queue': ['admin', 'property_manager', 'finance_controller', 'department_head', 'auditor'],
  '/audit-schedules': ['admin', 'property_manager', 'department_head', 'auditor'],
  '/property-location-registry': ['admin', 'property_manager'],
  '/room-kit-templates-stamping': ['admin', 'property_manager'],
  '/location-contents-sheet': ['admin', 'customer_admin', 'property_manager', 'department_head', 'storekeeper', 'finance_controller', 'auditor', 'field_user'],
  '/maintenance-incident-logging': ['admin', 'customer_admin', 'finance_controller', 'auditor', 'property_manager', 'department_head', 'storekeeper', 'field_user'],
  '/work-orders-repair-management': ['admin', 'customer_admin', 'finance_controller', 'auditor', 'property_manager', 'department_head', 'storekeeper', 'field_user'],
  '/banquet-event-kits-batch-reconciliation': ['admin', 'department_head', 'property_manager', 'storekeeper', 'auditor'],
  '/guest-loaners-overdue-tracker': ['admin', 'field_user', 'storekeeper', 'department_head', 'property_manager', 'auditor'],
  '/account-subscription-plans': ['admin', 'customer_admin'],
  '/integration-fabric-connectors': ['admin', 'customer_admin', 'property_manager'],
  '/developer-access': ['admin', 'customer_admin'],
  '/connected-platforms': ['admin', 'customer_admin', 'property_manager', 'finance_controller'],
};

/** True when `role` may open `path` (routes without an entry are open to any signed-in user). */
export const canOpen = (role, path) => !ROUTE_ROLES[path] || ROUTE_ROLES[path].includes(role);

const ROLE_LABELS = {
  admin: tk('Admin'),
  customer_admin: tk('Customer Admin'),
  property_manager: tk('Property Manager'),
  finance_controller: tk('Finance Controller'),
  auditor: tk('Auditor (read-only)'),
  field_user: tk('Field User'),
  storekeeper: tk('Storekeeper'),
  department_head: tk('Department Head'),
  user: tk('User'),
};

/** Human label for a role key (e.g. field_user -> Field User). English; show it with t(roleLabel(role)). */
export const roleLabel = (role) => ROLE_LABELS[role] || String(role || 'user').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
